import { useCallback, useEffect, useRef, useState } from "react";
import type { NavDomain, NavMode, NavPage } from "../../config/roleMenus";
import { Icon } from "../icons/Icon";
import { Avatar, Badge } from "../ui/avatar";
import BellNotification from "./BellNotification";
import "../../styles/components/layout.css";

export interface TopNavProps {
  /** 导航形态：tabs = 顶部页面页签（无侧栏）；domains = 顶部业务域页签 + 侧栏域内页面 */
  mode: NavMode;
  /** tabs 形态下渲染的可见页面 */
  pages: NavPage[];
  /** domains 形态下渲染的可见业务域 */
  domains: NavDomain[];
  /** 当前页面 key（用于页签高亮） */
  activeKey: string;
  /** 当前业务域 key（domains 形态高亮） */
  activeDomainKey: string;
  /** 外链入口（HR 门户），tabs 形态下作为最后一个页签 */
  externalPage?: NavPage;
  /**
   * 域 key → 角标数量（目前只有「待我审批」，挂在含 /approval 的域上）。
   * 数量为 0 或缺失时**不渲染**角标（复用 Badge：0 不显示、超 99 显示 99+）。
   */
  domainBadges?: Record<string, number>;
  /** 页面 key → 角标数量：tabs 形态没有域页签，角标直接挂在页面上（如「审批工作台」） */
  pageBadges?: Record<string, number>;
  isMobile: boolean;
  userName: string;
  userRole: string;
  onNavigate: (path: string) => void;
  onSelectDomain: (key: string) => void;
  onOpenMenu: () => void;
  onOpenExternal: (url: string) => void;
  onLogout: () => void;
}

/**
 * 顶栏：品牌（仅无侧栏形态）+ 导航页签 + 全局搜索/通知/用户菜单。
 *
 * 设计取舍（2026-09-23 导航重构）：
 *   · **不再显示页面标题与路径** —— 位置信息由页签高亮与侧栏承担，此前同一页面名会在
 *     顶栏标题、面包屑、页面 H1 三个位置重复出现；
 *   · 页面少（≤ 阈值）时页签直接列**页面**（参考工程的纯顶部形态）；页面多时列**业务域**，
 *     域内页面交给侧栏，避免顶部塞进二三十个页签；
 *   · 页签区横向可滚动，窄屏不换行、不抖动。
 */
export default function TopNav({
  mode,
  pages,
  domains,
  activeKey,
  activeDomainKey,
  externalPage,
  domainBadges,
  pageBadges,
  isMobile,
  userName,
  userRole,
  onNavigate,
  onSelectDomain,
  onOpenMenu,
  onOpenExternal,
  onLogout,
}: TopNavProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  // 当前业务域对象（窄屏顶栏用它显示「当前域名称」）
  const activeDomain = domains.find((item) => item.key === activeDomainKey) ?? null;

  useEffect(() => {
    if (!menuOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (!menuRef.current?.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [menuOpen]);

  // ── 二级浮层（2026-09-24：去掉桌面侧栏后，一级导航的配套）──────────────
  // 交互契约：悬停即开、移出延迟 120ms 收（斜向移动不闪断）；点击可切换开关（触摸/无 hover 回退）；
  // 键盘 ArrowDown 打开并聚焦条目、Esc 关闭并把焦点交还触发页签、左右键在域页签间移动；
  // 点击面板外部、页签横向滚动、窗口尺寸变化都会收起。
  const [openDomainKey, setOpenDomainKey] = useState<string | null>(null);
  const [anchor, setAnchor] = useState<{ left: number; top: number } | null>(null);
  const closeTimerRef = useRef<number | null>(null);
  const flyoutRef = useRef<HTMLDivElement | null>(null);
  const tabsRef = useRef<HTMLElement | null>(null);
  const triggerRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  /** 悬停打开面板的时刻：紧随其后的点击视为「同一次操作」，不切换收起 */
  const hoverOpenedAtRef = useRef(0);
  const openDomain = domains.find((item) => item.key === openDomainKey) ?? null;

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current !== null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const closePanel = useCallback(() => {
    clearCloseTimer();
    setOpenDomainKey(null);
  }, [clearCloseTimer]);

  const openPanel = useCallback(
    (domainKey: string, trigger?: HTMLElement | null) => {
      clearCloseTimer();
      const el = trigger ?? triggerRefs.current[domainKey];
      if (el) {
        const rect = el.getBoundingClientRect();
        setAnchor({ left: rect.left, top: rect.bottom + 6 });
      }
      setOpenDomainKey(domainKey);
    },
    [clearCloseTimer],
  );

  const scheduleClose = useCallback(() => {
    clearCloseTimer();
    closeTimerRef.current = window.setTimeout(() => setOpenDomainKey(null), 120);
  }, [clearCloseTimer]);

  /** 打开后把焦点送进面板：当前页优先，否则第一条 */
  const focusPanelItem = useCallback(() => {
    window.requestAnimationFrame(() => {
      const items = flyoutRef.current?.querySelectorAll<HTMLButtonElement>(".sider-item");
      if (!items?.length) return;
      const activeItem = flyoutRef.current?.querySelector<HTMLButtonElement>(".sider-item.is-active");
      (activeItem ?? items[0]).focus();
    });
  }, []);

  // 点击「面板与页签之外」的任何位置 → 收起
  useEffect(() => {
    if (!openDomainKey) return;
    const onDocDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (flyoutRef.current?.contains(target)) return;
      if (tabsRef.current?.contains(target)) return; // 页签自身的开关由 onClick 处理
      closePanel();
    };
    document.addEventListener("mousedown", onDocDown);
    return () => document.removeEventListener("mousedown", onDocDown);
  }, [openDomainKey, closePanel]);

  // Esc 关闭（焦点归还触发页签）；窗口尺寸变化与页签横滑时收起
  useEffect(() => {
    if (!openDomainKey) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      closePanel();
      triggerRefs.current[openDomainKey]?.focus();
    };
    const tabsEl = tabsRef.current;
    document.addEventListener("keydown", onKeyDown);
    window.addEventListener("resize", closePanel);
    tabsEl?.addEventListener("scroll", closePanel);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("resize", closePanel);
      tabsEl?.removeEventListener("scroll", closePanel);
    };
  }, [openDomainKey, closePanel]);

  // 说明：这里**刻意不监听路由变化去关面板**。条目点击本身已显式 closePanel()；而路由提交可能被
  // React 的并发渲染推迟数百毫秒（懒加载页面分片时尤其明显），那样会在「悬停已展开面板」之后补一枪
  // 把面板关掉 —— 线上验收走查里表现为随机的「读不到二级页面」。
  // 收起时机只保留：移出页签（延迟 120ms）、点击面板外部、Esc、点击条目。

  useEffect(() => () => clearCloseTimer(), [clearCloseTimer]);

  const openSearch = () => window.dispatchEvent(new CustomEvent("open-global-search"));

  return (
    <header className="app-topnav">
      <div className="topnav-left">
        {mode === "domains" && isMobile ? (
          <>
            <button type="button" className="topnav-icon-btn" aria-label="打开菜单" onClick={onOpenMenu}>
              <Icon name="menu" size={18} />
            </button>
            {/* 窄屏不显示域页签（实测只剩 139px，必须横滑），改为「当前域名称」+ 抽屉里的域切换 */}
            <span className="topnav-mobile-domain" title={activeDomain?.label}>
              {activeDomain?.short || activeDomain?.label || ""}
            </span>
          </>
        ) : null}
        {mode === "tabs" ? (
          <button type="button" className="topnav-brand" onClick={() => onNavigate("/")} title="返回综合驾驶舱">
            <span className="topnav-brand-logo">C</span>
            <span className="topnav-brand-name">CoSeek 企业OS</span>
          </button>
        ) : null}
      </div>

      {/* 窄屏：页面导航下沉到顶部底栏（tabs 形态）、业务域切换进抽屉（domains 形态），顶栏不再挤横滑页签 */}
      <nav
        ref={tabsRef}
        className={`topnav-tabs${isMobile ? " is-mobile-hidden" : ""}`}
        aria-label={mode === "tabs" ? "页面导航" : "业务域导航"}
      >
        {mode === "tabs"
          ? pages.map((page) => (
              <button
                key={page.key}
                type="button"
                className={`topnav-tab${page.key === activeKey ? " is-active" : ""}`}
                aria-current={page.key === activeKey ? "page" : undefined}
                onClick={() => onNavigate(page.key)}
              >
                {/* 角标走 Badge：数量 0 不渲染、超 99 显示 99+（与顶栏铃铛同口径） */}
                <Badge count={pageBadges?.[page.key] ?? 0}>
                  {page.icon}
                  <span>{page.label}</span>
                </Badge>
              </button>
            ))
          : domains.map((domain) => {
              const isOpen = openDomainKey === domain.key;
              // 单页域（如财务）不存在「选哪个」的问题：点击直接进，也不做悬停弹层
              const single = domain.pages.length === 1;
              return (
                <button
                  key={domain.key}
                  type="button"
                  ref={(el) => {
                    triggerRefs.current[domain.key] = el;
                  }}
                  className={`topnav-tab topnav-tab-trigger${domain.key === activeDomainKey ? " is-active" : ""}${
                    isOpen ? " is-open" : ""
                  }`}
                  title={domain.label}
                  // 单页域没有二级面板，不声明 popup 语义（验收脚本也据此识别单页域）
                  aria-haspopup={single ? undefined : "true"}
                  aria-expanded={single ? undefined : isOpen}
                  aria-controls={isOpen ? `topnav-flyout-${domain.key}` : undefined}
                  onMouseEnter={
                    single
                      ? undefined
                      : (e) => {
                          hoverOpenedAtRef.current = Date.now();
                          openPanel(domain.key, e.currentTarget);
                        }
                  }
                  onMouseLeave={single ? undefined : scheduleClose}
                  onClick={(e) => {
                    if (single) {
                      onSelectDomain(domain.key); // 顺带记住当前域
                      closePanel();
                      return;
                    }
                    if (isOpen) {
                      // 悬停刚把面板打开时的这次点击不该把它收起（鼠标用户 hover 后顺手一点很常见；
                      // 触摸端首次 tap 也会先触发一次 hover 语义），超过 500ms 才视为「再点一次收起」
                      if (Date.now() - hoverOpenedAtRef.current < 500) return;
                      closePanel();
                      return;
                    }
                    openPanel(domain.key, e.currentTarget);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "ArrowDown") {
                      e.preventDefault();
                      openPanel(domain.key, e.currentTarget);
                      focusPanelItem();
                      return;
                    }
                    if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
                      // 左右键在域页签之间移动焦点（顺序与视觉一致）
                      e.preventDefault();
                      const keys = domains.map((item) => item.key);
                      const index = keys.indexOf(domain.key);
                      const nextIndex =
                        e.key === "ArrowRight" ? (index + 1) % keys.length : (index - 1 + keys.length) % keys.length;
                      triggerRefs.current[keys[nextIndex]]?.focus();
                    }
                  }}
                >
                  <Badge count={domainBadges?.[domain.key] ?? 0}>
                    {domain.icon}
                    <span>{isMobile ? domain.short : domain.label}</span>
                  </Badge>
                </button>
              );
            })}
        {mode === "tabs" && externalPage?.externalUrl ? (
          <button type="button" className="topnav-tab is-external" onClick={() => onOpenExternal(externalPage.externalUrl as string)}>
            <span>{externalPage.label}</span>
            <Icon name="link" size={12} />
          </button>
        ) : null}
      </nav>

      {/* 二级浮层：fixed + 实测锚点，避开 .topnav-tabs 的 overflow 裁切 */}
      {openDomain && !isMobile ? (
        <div
          id={`topnav-flyout-${openDomain.key}`}
          ref={flyoutRef}
          className="topnav-flyout"
          role="menu"
          aria-label={`${openDomain.label}页面`}
          style={anchor ? { left: anchor.left, top: anchor.top } : undefined}
          onMouseEnter={clearCloseTimer}
          onMouseLeave={scheduleClose}
        >
          {openDomain.pages.map((page) => {
            const active = page.key === activeKey;
            const badge = pageBadges?.[page.key] ?? 0;
            return (
              <button
                key={page.key}
                type="button"
                role="menuitem"
                className={`sider-item${active ? " is-active" : ""}`}
                aria-current={active ? "page" : undefined}
                onClick={() => {
                  closePanel();
                  onNavigate(page.key);
                }}
                onKeyDown={(e) => {
                  const items = Array.from(flyoutRef.current?.querySelectorAll<HTMLButtonElement>(".sider-item") ?? []);
                  const index = items.indexOf(e.currentTarget);
                  if (e.key === "ArrowDown") {
                    e.preventDefault();
                    items[(index + 1) % items.length]?.focus();
                  } else if (e.key === "ArrowUp") {
                    e.preventDefault();
                    items[(index - 1 + items.length) % items.length]?.focus();
                  } else if (e.key === "Escape") {
                    e.preventDefault();
                    closePanel();
                    triggerRefs.current[openDomain.key]?.focus();
                  }
                }}
              >
                <span className="sider-item-icon">{page.icon}</span>
                <span className="sider-item-label">{page.label}</span>
                {badge > 0 ? <span className="sider-item-badge">{badge > 99 ? "99+" : badge}</span> : null}
              </button>
            );
          })}
        </div>
      ) : null}

      <div className="topnav-right">
        <button type="button" className="topnav-search" onClick={openSearch} title="全局搜索（Ctrl/⌘ + K）">
          <Icon name="search" size={15} />
          <span>搜索物料 / 订单 / 客户</span>
          <kbd>⌘K</kbd>
        </button>

        <BellNotification onOpenAll={() => onNavigate("/notifications")} />

        <div className="topnav-user-wrap" ref={menuRef}>
          <button type="button" className="topnav-user" onClick={() => setMenuOpen((v) => !v)} aria-expanded={menuOpen}>
            <Avatar name={userName} size={30} />
            <span className="topnav-user-meta">
              <span className="topnav-user-name">{userName}</span>
              <span className="topnav-user-role">{userRole || "未分配角色"}</span>
            </span>
            <Icon name="chevron-down" size={13} style={{ color: "var(--mut)" }} />
          </button>

          {menuOpen ? (
            <div className="topnav-menu" role="menu">
              <button
                type="button"
                className="topnav-menu-item"
                onClick={() => {
                  setMenuOpen(false);
                  onNavigate("/screen");
                }}
              >
                <Icon name="fullscreen" size={15} />
                全屏大屏
              </button>
              <button
                type="button"
                className="topnav-menu-item"
                onClick={() => {
                  setMenuOpen(false);
                  window.dispatchEvent(new CustomEvent("show-onboarding"));
                }}
              >
                <Icon name="compass" size={15} />
                新手引导
              </button>
              {externalPage?.externalUrl ? (
                // HR 门户：原挂在桌面侧栏底部；侧栏移除后迁到用户菜单，保证外链仍可达
                <button
                  type="button"
                  className="topnav-menu-item"
                  onClick={() => {
                    setMenuOpen(false);
                    onOpenExternal(externalPage.externalUrl as string);
                  }}
                >
                  <Icon name="link" size={15} />
                  {externalPage.label}
                </button>
              ) : null}
              <div className="topnav-menu-sep" />
              <button
                type="button"
                className="topnav-menu-item is-danger"
                onClick={() => {
                  setMenuOpen(false);
                  onLogout();
                }}
              >
                <Icon name="logout" size={15} />
                退出登录
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </header>
  );
}
